import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import html2canvas from "html2canvas-pro";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Share2,
  Facebook,
  Linkedin,
  Twitter,
  Instagram,
  Mail,
  Link as LinkIcon,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { Event } from "@/data/events";
import { useWeeklySpeakers, speakerKey } from "@/hooks/useWeeklySpeakers";
import SpeakerCard from "@/components/SpeakerCard";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

interface ShareEventDialogProps {
  event: Event;
}

const SOCIAL_CARD_PREVIEW_SIZE = 340;
const SOCIAL_CARD_CAPTURE_SCALE = 3;
const SOCIAL_IMAGE_WIDTH = 1200;
const SOCIAL_IMAGE_HEIGHT = 630;

const waitForRenderedImages = async (node: HTMLElement) => {
  const images = Array.from(node.querySelectorAll("img"));
  await Promise.all(
    images.map(async (img) => {
      if (img.complete && img.naturalWidth > 0) return;
      try {
        await img.decode();
      } catch {
        // html2canvas can still render the fallback/loaded state if decode fails.
      }
    }),
  );
};

const createFacebookSafeCardImage = async (node: HTMLElement) => {
  await document.fonts?.ready;
  await waitForRenderedImages(node);

  const cardCanvas = await html2canvas(node, {
    backgroundColor: "#ffffff",
    scale: SOCIAL_CARD_CAPTURE_SCALE,
    useCORS: true,
  });

  const socialCanvas = document.createElement("canvas");
  socialCanvas.width = SOCIAL_IMAGE_WIDTH;
  socialCanvas.height = SOCIAL_IMAGE_HEIGHT;

  const ctx = socialCanvas.getContext("2d");
  if (!ctx) return cardCanvas;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, SOCIAL_IMAGE_WIDTH, SOCIAL_IMAGE_HEIGHT);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const cardSize = SOCIAL_IMAGE_HEIGHT;
  const x = Math.round((SOCIAL_IMAGE_WIDTH - cardSize) / 2);
  ctx.drawImage(cardCanvas, x, 0, cardSize, cardSize);

  return socialCanvas;
};

const ShareEventDialog = ({ event }: ShareEventDialogProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const pathChapter: "washington" | "union" =
    typeof window !== "undefined" && window.location.pathname.startsWith("/union")
      ? "union"
      : "washington";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://rcnmo.com";

  const isWeeklyChapter =
    event.type === "weekly" && (event.chapter === "washington" || event.chapter === "union");
  const speakerChapter: "washington" | "union" | undefined = isWeeklyChapter
    ? (event.chapter as "washington" | "union")
    : undefined;

  const { speakers } = useWeeklySpeakers(open ? speakerChapter : undefined);

  const occurrenceDate = `${event.date.getFullYear()}-${String(event.date.getMonth() + 1).padStart(2, "0")}-${String(event.date.getDate()).padStart(2, "0")}`;
  const speakerRow = speakerChapter
    ? speakers[speakerKey(speakerChapter, occurrenceDate)]
    : undefined;

  const SHARE_BASE = "https://share.rcnmo.com";
  // Unique per dialog-open so social crawlers (Facebook/LinkedIn) re-scrape
  // instead of serving a stale preview cached from an earlier share.
  const SHARE_VERSION = useMemo(
    () => (open ? Date.now().toString(36) : "static"),
    [open],
  );
  const shareUrl = event.dbEventId
    ? `${SHARE_BASE}/event-share?id=${event.dbEventId}&date=${occurrenceDate}&chapter=${pathChapter}&v=${SHARE_VERSION}`
    : `${SHARE_BASE}/event-share?date=${occurrenceDate}&chapter=${pathChapter}&v=${SHARE_VERSION}`;


  const hasFlyer = !!event.imageUrl;
  const hasSpeakerCard = !hasFlyer && !!speakerChapter && !!speakerRow;
  const hasAnyImage = hasFlyer || hasSpeakerCard;

  const dateLine = format(event.date, "EEEE, MMMM d, yyyy");

  const presenterLine = useMemo(() => {
    if (!speakerRow) return "";
    if (speakerRow.member?.name) {
      const topic = speakerRow.topic ? ` — ${speakerRow.topic}` : "";
      return `Presenter: ${speakerRow.member.name}${topic}`;
    }
    if (speakerRow.slot_type === "breakout") {
      return speakerRow.topic
        ? `Breakout session — ${speakerRow.topic}`
        : "Member-led breakout discussion";
    }
    if (speakerRow.slot_type === "guest") {
      return speakerRow.topic ? `Guest speaker — ${speakerRow.topic}` : "Guest speaker";
    }
    return "";
  }, [speakerRow]);

  const defaultMessage = useMemo(() => {
    return [
      `Join us for ${event.title}`,
      `${dateLine} • ${event.startTime} - ${event.endTime}`,
      event.location,
      presenterLine,
    ]
      .filter((l) => l !== "")
      .join("\n\n");
  }, [event, dateLine, presenterLine]);


  const [message, setMessage] = useState("");
  const [userEdited, setUserEdited] = useState(false);

  const fullPost = `${message}\n\n${shareUrl}`;

  useEffect(() => {
    if (!open) return;
    if (!userEdited) setMessage(defaultMessage);
  }, [open, defaultMessage, userEdited]);

  useEffect(() => {
    if (!open) {
      setMessage("");
      setUserEdited(false);
    }
  }, [open]);

  // When the share dialog opens for a weekly meeting that uses a SpeakerCard
  // (no event flyer), snapshot the rendered card and upload it to the
  // `speaker-cards` bucket so the event-share function can use it as og:image.
  const [cardUploading, setCardUploading] = useState(false);
  const [cardReady, setCardReady] = useState(false);
  useEffect(() => {
    if (!open || !hasSpeakerCard || !speakerChapter) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const node = previewRef.current;
      if (!node || cancelled) return;
      try {
        setCardUploading(true);
        setCardReady(false);
        const canvas = await createFacebookSafeCardImage(node);
        const blob: Blob | null = await new Promise((res) =>
          canvas.toBlob((b) => res(b), "image/png", 0.95),
        );
        if (!blob || cancelled) return;
        const form = new FormData();
        form.append("chapter", speakerChapter);
        form.append("date", occurrenceDate);
        form.append("version", SHARE_VERSION);
        form.append("file", new File([blob], "card.png", { type: "image/png" }));
        const { error } = await supabase.functions.invoke("upload-speaker-card", {
          body: form,
        });
        if (error) throw error;
        if (!cancelled) setCardReady(true);
      } catch (err) {
        console.error("speaker card upload failed", err);
        if (!cancelled) toast.error("Couldn't prepare social preview image");
      } finally {
        if (!cancelled) setCardUploading(false);
      }
    }, 400); // wait for SpeakerCard images/fonts to settle
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, hasSpeakerCard, speakerChapter, occurrenceDate, SHARE_VERSION, speakerRow?.member?.image, speakerRow?.member?.name, speakerRow?.topic]);


  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedMessage = encodeURIComponent(fullPost);

  const subject = `${event.title} — ${dateLine}`;
  const encodedSubject = encodeURIComponent(subject);

  const platforms = [
    {
      name: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: "X (Twitter)",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedMessage}`,
    },
    {
      name: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodedSubject}&body=${encodedMessage}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullPost);
      toast.success("Post copied to clipboard");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const handleInstagram = async () => {
    try {
      await navigator.clipboard.writeText(fullPost);
      toast.success("Post copied — paste into Instagram");
    } catch {
      toast.error("Failed to copy");
    }
  };


  const handleDownloadImage = async () => {
    try {
      if (hasFlyer && event.imageUrl) {
        const link = document.createElement("a");
        link.href = event.imageUrl;
        link.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${occurrenceDate}.jpg`;
        link.target = "_blank";
        link.rel = "noopener";
        link.click();
        return;
      }
      if (!previewRef.current) return;
      const canvas = await createFacebookSafeCardImage(previewRef.current);
      const link = document.createElement("a");
      link.download = `${event.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${occurrenceDate}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Image downloaded — attach it to your post");
    } catch (err) {
      console.error(err);
      toast.error("Failed to download image");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl w-[calc(100vw-1rem)] sm:w-full max-h-[90dvh] overflow-y-auto p-4 sm:p-6"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Share this event</DialogTitle>
          <DialogDescription>
            Review the preview, edit the post text, then pick a platform. The image
            shown will be used as the social-media preview.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:gap-6 md:grid-cols-2 mt-2">
          <div className="space-y-2 order-2 md:order-1">
            <Label htmlFor="share-message">Post text</Label>
            <Textarea
              id="share-message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setUserEdited(true);
              }}
              rows={8}
              className="font-sans text-sm"
            />
            <div className="rounded-md border bg-muted/40 px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                Share link (locked)
              </p>
              <p className="text-xs font-mono break-all text-foreground/80 select-all">
                {shareUrl}
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              {isMobile
                ? "Tap Share to open your device's share sheet."
                : "Edits apply to X, Email, Instagram, and Copy post. Facebook and LinkedIn strip pre-filled text — they generate their own preview from the shared link."}
            </p>

          </div>

          <div className="space-y-3 order-1 md:order-2">
            <Label>Image preview</Label>
            {hasFlyer ? (
              <div className="rounded-lg border bg-muted/30 p-3 flex justify-center">
                <img
                  src={event.imageUrl}
                  alt={`${event.title} flyer`}
                  className="max-h-[360px] w-auto rounded-md object-contain"
                />
              </div>
            ) : hasSpeakerCard && speakerChapter && speakerRow ? (
              <div className="rounded-lg border bg-muted/30 p-3">
                <div ref={previewRef} className="w-full mx-auto aspect-square overflow-hidden rounded-xl [&>*]:h-full" style={{ maxWidth: `${SOCIAL_CARD_PREVIEW_SIZE}px` }}>
                  <SpeakerCard
                    chapter={speakerChapter}
                    slotType={speakerRow.slot_type}
                    member={speakerRow.member ?? null}
                    topic={speakerRow.topic}
                    dateLabel={dateLine}
                    timeLabel={`${event.startTime} - ${event.endTime}`}
                    locationLabel={event.location}
                  />
                </div>
              </div>
            ) : null}
            {hasSpeakerCard && (
              <p className="text-xs text-muted-foreground">
                {cardUploading
                  ? "Preparing social preview image…"
                  : cardReady
                  ? "Social preview image ready."
                  : "Open this dialog for a moment so the preview image can render."}
              </p>
            )}
            {!hasFlyer && !hasSpeakerCard && (
              <div className="rounded-lg border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                No image for this event. The link preview will use your post text only.
              </div>
            )}
            {hasAnyImage && (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-auto min-h-9 py-2 whitespace-normal text-center leading-tight"
                onClick={handleDownloadImage}
              >
                <Download className="w-4 h-4 mr-2 shrink-0" />
                <span>Download image (to attach on Facebook/Instagram)</span>
              </Button>
            )}

          </div>
        </div>

        <div className="mt-4 border-t pt-4 space-y-2">
          {hasSpeakerCard && !cardReady && (
            <p className="text-xs text-muted-foreground text-right">
              Preparing preview image… sharing will enable in a moment.
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-nowrap sm:gap-1.5 sm:justify-end">
          {isMobile ? (
            <Button
              variant="default"
              size="sm"
              className="col-span-3 gap-1 px-3 h-9 text-sm"
              disabled={hasSpeakerCard && !cardReady}
              onClick={async () => {
                if (typeof navigator !== "undefined" && navigator.share) {
                  try {
                    await navigator.share({
                      title: event.title,
                      text: fullPost,
                      url: shareUrl,
                    });
                    return;
                  } catch (err: any) {
                    if (err?.name === "AbortError") return;
                  }
                }
                try {
                  await navigator.clipboard.writeText(fullPost);
                  toast.success("Sharing unavailable — post copied to clipboard");
                } catch {
                  toast.error("Couldn't share or copy — please try Copy post");
                }
              }}
            >
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          ) : (
            <>
              {platforms.map((p) => {
                const stripsText = p.name === "Facebook" || p.name === "LinkedIn";
                return (
                  <Button
                    key={p.name}
                    variant="outline"
                    size="sm"
                    className="gap-1 px-3 sm:px-2 h-9 sm:h-8 text-sm sm:text-xs"
                    disabled={hasSpeakerCard && !cardReady}
                    onClick={async () => {
                      if (stripsText) {
                        try {
                          await navigator.clipboard.writeText(fullPost);
                          toast.success(`Post copied — paste it into ${p.name} after the preview loads`);
                        } catch {
                          toast.error("Couldn't copy post text — please copy it manually");
                        }
                      }
                      window.open(p.href, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <p.icon className="w-3.5 h-3.5" />
                    {p.name}
                  </Button>
                );
              })}
              <Button variant="outline" size="sm" onClick={handleInstagram} disabled={hasSpeakerCard && !cardReady} className="gap-1 px-3 sm:px-2 h-9 sm:h-8 text-sm sm:text-xs">
                <Instagram className="w-3.5 h-3.5" />
                Instagram
              </Button>
            </>
          )}

          <Button variant="outline" size="sm" onClick={handleCopy} disabled={hasSpeakerCard && !cardReady} className="gap-1 px-3 sm:px-2 h-9 sm:h-8 text-sm sm:text-xs">
            <LinkIcon className="w-3.5 h-3.5" />
            Copy post
          </Button>
          <Button variant="outline" size="sm" onClick={handleCopyLink} disabled={hasSpeakerCard && !cardReady} className="gap-1 px-3 sm:px-2 h-9 sm:h-8 text-sm sm:text-xs">
            <LinkIcon className="w-3.5 h-3.5" />
            Copy link
          </Button>
          </div>
        </div>
      </DialogContent>

    </Dialog>
  );
};

export default ShareEventDialog;
